import { useCallback, useEffect, useRef, useState } from 'react'
import { MdMeetingRoom, MdSearch, MdSearchOff } from 'react-icons/md'
import { toast } from 'react-toastify'
import { useDebounce } from '@/lib/hooks/use-debounce'
import {
    APPOINTMENT_KIND_LABELS,
    presenceBadgeProps,
    PresenceEntry,
    searchPresenceHistory,
} from '@/lib/api/presence/presence-api'
import PaginationControls from '@/components/pagination/PaginationControls'
import PresenceEventList from './presence-event-list'

/** Passagens por página. */
const PAGE_LIMIT = 20

/** Quanto tempo o paciente ficou (ou está) no balcão nesta passagem. */
function elapsed(entry: PresenceEntry): string {
    const start = new Date(entry.arrivedAt).getTime()
    const end = entry.leftAt ? new Date(entry.leftAt).getTime() : Date.now()
    const minutes = Math.max(0, Math.floor((end - start) / 60000))
    if (minutes < 60) return `${minutes} min`
    return `${Math.floor(minutes / 60)}h ${minutes % 60}min`
}

function formatDay(value: string): string {
    return new Date(value).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    })
}

/** Agendamento que originou a passagem. */
function originLabel(entry: PresenceEntry): string | null {
    if (entry.consultation) {
        return `${APPOINTMENT_KIND_LABELS.CONSULTATION}: ${
            entry.consultation.title || APPOINTMENT_KIND_LABELS.CONSULTATION
        }`
    }
    if (entry.exam) {
        return `${APPOINTMENT_KIND_LABELS.EXAM}: ${
            entry.exam.title || APPOINTMENT_KIND_LABELS.EXAM
        }`
    }
    if (entry.order) {
        return `${APPOINTMENT_KIND_LABELS.ORDER}: ${
            entry.order.title || APPOINTMENT_KIND_LABELS.ORDER
        }`
    }
    return null
}

/**
 * Busca no histórico do balcão: "como e onde a Maria foi atendida?".
 *
 * Diferente das outras duas abas, aqui a chave é a PESSOA, e não o momento nem
 * um agendamento específico. Abre já com as passagens recentes — a pergunta
 * costuma ser sobre quem acabou de sair, e obrigar a digitar o nome atrasaria a
 * resposta que alguém está esperando no telefone.
 */
export default function PresenceHistory() {
    const [search, setSearch] = useState('')
    const debouncedSearch = useDebounce(search, 400)
    const [entries, setEntries] = useState<PresenceEntry[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(true)
    /** Só a resposta do pedido mais recente é aplicada. */
    const requestRef = useRef(0)

    const load = useCallback(async () => {
        const requestId = ++requestRef.current
        setLoading(true)
        const response = await searchPresenceHistory({
            search: debouncedSearch.trim() || undefined,
            limit: PAGE_LIMIT,
            page,
        })
        if (requestId !== requestRef.current) return
        setLoading(false)
        if (!response.success) {
            toast.error(
                response.error || 'Não foi possível carregar o histórico'
            )
            return
        }
        setEntries(response.result?.items ?? [])
        setTotal(response.result?.total ?? 0)
    }, [debouncedSearch, page])

    useEffect(() => {
        setPage(1)
    }, [debouncedSearch])

    useEffect(() => {
        load()
    }, [load])

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
                <label
                    className="text-sm font-medium"
                    htmlFor="presence-history-search"
                >
                    Buscar paciente no histórico
                </label>
                <label className="input input-bordered flex items-center gap-2 !rounded-lg">
                    <MdSearch className="text-lg text-base-content/50" />
                    <input
                        id="presence-history-search"
                        type="text"
                        className="grow"
                        placeholder="Nome ou CPF"
                        value={search}
                        onChange={event => setSearch(event.target.value)}
                    />
                </label>
                <p className="text-xs text-base-content/60">
                    Passagens pelo balcão nos últimos 7 dias, das mais recentes
                    para as mais antigas.
                </p>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-10">
                    <span className="loading loading-spinner loading-md" />
                </div>
            ) : entries.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-10 text-center">
                    <MdSearchOff className="text-4xl text-base-content/30" />
                    <p className="text-sm font-medium">Nada no histórico</p>
                    <p className="text-xs text-base-content/60">
                        Ninguém passou pelo controle de presença no período.
                    </p>
                </div>
            ) : (
                <>
                    <ul className="flex flex-col gap-3">
                        {entries.map(entry => {
                            const badge = presenceBadgeProps(entry.status.color)
                            const origin = originLabel(entry)
                            return (
                                <li
                                    key={entry.id}
                                    className="rounded-xl border border-base-200 p-3"
                                >
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-semibold">
                                            {entry.client.name}
                                        </span>
                                        <span
                                            className={`badge badge-sm ${badge.className}`}
                                            style={badge.style}
                                        >
                                            {entry.status.name}
                                        </span>
                                        {entry.room && (
                                            <span className="flex items-center gap-1 text-xs text-base-content/70">
                                                <MdMeetingRoom className="text-sm" />
                                                {entry.room.name}
                                            </span>
                                        )}
                                    </div>

                                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-base-content/60">
                                        {origin && <span>{origin}</span>}
                                        <span>
                                            Chegou {formatDay(entry.arrivedAt)}
                                        </span>
                                        <span>
                                            {entry.leftAt
                                                ? `Permaneceu ${elapsed(entry)}`
                                                : `No balcão há ${elapsed(entry)}`}
                                        </span>
                                    </div>

                                    <PresenceEventList
                                        events={entry.events ?? []}
                                    />
                                </li>
                            )
                        })}
                    </ul>

                    <PaginationControls
                        page={page}
                        limit={PAGE_LIMIT}
                        total={total}
                        loading={loading}
                        onPageChange={setPage}
                        label="passagem"
                    />
                </>
            )}
        </div>
    )
}
